
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export interface Season {
  id: string;
  season_year: number;
  name: string;
  club_id: string;
  status: 'active' | 'upcoming' | 'inactive';
  created_at: string;
}

export const useSeasons = (clubId?: string, coachOnly = false) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();

  const fetchSeasons = useCallback(async () => {
    if (!clubId) {
      setSeasons([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const query = supabase
        .from('seasons')
        .select('*')
        .eq('club_id', clubId)
        .order('season_year', { ascending: false });

      // Since we no longer have 'coach' role, we'll skip coach-specific filtering
      // All users can see all seasons for a club
      
      const { data, error } = await query;

      if (error) {
        console.error('Error fetching seasons:', error);
        toast.error('Failed to load seasons');
        return;
      }

      setSeasons(data || []);
    } catch (error) {
      console.error('Error fetching seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  return {
    seasons,
    loading,
    fetchSeasons
  };
};
